import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { HtsAirCfarOffersSelectors } from "@b2bportal/core-flights-cfar-offers";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { createPaxPricingWithCfar } from "@tenant/core-flights-cfar-offers";
import {
  ReviewBreakdown,
  type ReviewFlightSecondaryContentProps,
} from "@tenant/flights";
import {
  DialogTrigger,
  Icon,
  InfoBanner,
  InfoBannerSeverity,
} from "@tenant/ui";
import clsx from "clsx";
import { StaysUpsellDialog } from "../StaysUpsellDialog/StaysUpsellDialog";
import defaultStyles from "./ReviewFlightSecondaryContent.module.scss";
import { useStaysUpsell } from "./useStaysUpsell";
import { DOCKED_CTA_HEIGHT_PROPERTY } from "@/components/Layout/dockedCta";
import { ChildAgesDialog } from "@/overrides/packages/PackageTravellerPicker";
import { useIsExchangeFlow } from "@/utilities/flights/useIsExchangeFlow";
import { usePublishedHeight } from "@/utilities/hooks/usePublishedHeight";

// From core-flights ReviewFlightSecondaryContent component
export const ReviewFlightSecondaryContent = (
  props: ReviewFlightSecondaryContentProps
) => {
  const { className, paxPricing, tripType, onClick } = props;
  const COMPONENT_KEY = CoreFlightsComponent.ReviewFlightSecondaryContent;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  // Below lg this is a fixed bottom bar, so the page must reserve room for it.
  const rootRef = useRef<HTMLDivElement>(null);
  usePublishedHeight(rootRef, "--review-secondary-content-height");
  usePublishedHeight(rootRef, DOCKED_CTA_HEIGHT_PROPERTY);

  const cfarOfferWithSelectedStatus = useSelector(
    HtsAirCfarOffersSelectors.selectCfarOfferWithSelectedStatus
  );

  const isExchange = useIsExchangeFlow();
  const {
    variant,
    variants,
    isEnabled,
    isDialogOpen,
    childAgesGate,
    openDialog,
    handleConfirm,
    handleDecline,
    handleDismiss,
  } = useStaysUpsell();

  const effectivePaxPricing = useMemo(() => {
    if (cfarOfferWithSelectedStatus?.offer) {
      return createPaxPricingWithCfar(
        paxPricing,
        cfarOfferWithSelectedStatus.offer
      );
    }
    return paxPricing;
  }, [paxPricing, cfarOfferWithSelectedStatus]);

  return (
    <div ref={rootRef} className={clsx(block, className)}>
      {isEnabled && (
        <>
          <StaysUpsellDialog
            open={isDialogOpen}
            onOpen={openDialog}
            onDecline={handleDecline}
            onDismiss={handleDismiss}
            onConfirm={handleConfirm}
          >
            {variant === variants.sticky_bar && (
              <DialogTrigger asChild>
                <button type="button" className={cn("upsell-banner")}>
                  <Icon
                    iconName={IconNameEnum.stays__active__with_background}
                    className={cn("upsell-banner-icon")}
                  />
                  <span className={cn("upsell-banner-text")}>
                    {t("flightReview.addStayDialog.title")}
                  </span>
                  <Icon
                    iconName={IconNameEnum.chevronRight}
                    className={cn("upsell-banner-chevron")}
                  />
                </button>
              </DialogTrigger>
            )}
          </StaysUpsellDialog>
          <ChildAgesDialog
            open={childAgesGate.isOpen}
            childrenCount={childAgesGate.childrenCount}
            infantsOnLapCount={childAgesGate.infantsOnLapCount}
            infantsInSeatCount={childAgesGate.infantsInSeatCount}
            onConfirm={childAgesGate.onConfirm}
            onCancel={childAgesGate.onCancel}
          />
        </>
      )}
      <ReviewBreakdown
        passengerPricing={effectivePaxPricing}
        tripType={tripType}
        className={cn("review-breakdown")}
      />
      <button type="button" className={cn("continue-button")} onClick={onClick}>
        {t("continue")}
      </button>
      {!isExchange && (
        <InfoBanner
          className={cn("info-banner")}
          severity={InfoBannerSeverity.INFO}
          icon={IconNameEnum.infoFilled}
        >
          {t("reviewChoiceOfPaymentAtCheckout")}
        </InfoBanner>
      )}
    </div>
  );
};
